"use client";

import {
  ChangeEvent,
  CSSProperties,
  useEffect,
  useRef,
  useState,
} from "react";
import Link from "next/link";
import classNames from "classnames";
import { IoPause, IoPlay } from "react-icons/io5";
import { CollectionItem } from "@/@types";
import { defineImageUrl } from "@/utils";
import styles from "./Collection.module.scss";

interface Props {
  collection: CollectionItem;
  /** Vídeo tocando completo, com som e barra de controles */
  isPlaying: boolean;
  onPlay: () => void;
  onStop: () => void;
}

const canHover = () =>
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const formatTime = (seconds: number) => {
  const total = Math.floor(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

export default function Collection({
  collection,
  isPlaying,
  onPlay,
  onStop,
}: Props) {
  const { title, uuid, url } = collection;
  const image = defineImageUrl(collection.image?.url ?? undefined);
  const avatar = defineImageUrl(collection.avatar?.url ?? undefined);
  const video = defineImageUrl(collection.video?.url ?? undefined);
  const href = url || `/colecao/a/${uuid}`;

  const videoRef = useRef<HTMLVideoElement>(null);
  const [paused, setPaused] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // Vídeo de fato exibindo frames (evento playing, depois de um eventual
  // seek): só então a capa sai, sem flash do frame antigo
  const [showingVideo, setShowingVideo] = useState(false);
  // Espelha isPlaying, mas já vira true no clique, antes do re-render
  const playingRef = useRef(isPlaying);
  playingRef.current = isPlaying;

  // play() rejeita (AbortError) quando o pause() chega antes do vídeo carregar
  const playPreview = () => {
    const element = videoRef.current;
    if (!element || prefersReducedMotion()) return;
    element.muted = true;
    element.play().catch(() => {});
  };

  // Desktop: cada hover roda o vídeo inteiro do início, mudo e em loop; ao
  // sair, pausa e a capa volta
  const handleEnter = () => {
    const element = videoRef.current;
    if (!element || playingRef.current || !canHover()) return;
    element.currentTime = 0;
    playPreview();
  };

  const handleLeave = () => {
    if (!playingRef.current && canHover()) videoRef.current?.pause();
  };

  const togglePause = () => {
    const element = videoRef.current;
    if (!element) return;
    if (element.paused) {
      element.play().catch(() => {});
    } else {
      element.pause();
    }
  };

  // Precisa rodar dentro do clique: é o gesto do usuário que libera o áudio.
  // Parado ou rodando mudo (hover/mobile), começa do início com som; tocando
  // com som, alterna play/pause.
  const handlePlayButton = () => {
    const element = videoRef.current;
    if (!element) return;

    if (playingRef.current) {
      togglePause();
      return;
    }

    playingRef.current = true;
    element.currentTime = 0;
    element.muted = false;
    element.play().catch(() => {});
    onPlay();
  };

  const handleSeek = (event: ChangeEvent<HTMLInputElement>) => {
    const element = videoRef.current;
    if (!element) return;
    element.currentTime = Number(event.target.value);
    setCurrentTime(element.currentTime);
  };

  const handleTimeUpdate = () => {
    const element = videoRef.current;
    if (element && playingRef.current) setCurrentTime(element.currentTime);
  };

  // Ao sair do modo completo (outro card tocou, terminou ou saiu da tela),
  // volta mudo, parado no início
  const wasPlaying = useRef(false);
  useEffect(() => {
    const element = videoRef.current;
    if (element && wasPlaying.current && !isPlaying) {
      element.pause();
      element.muted = true;
      element.currentTime = 0;
      setCurrentTime(0);
    }
    wasPlaying.current = isPlaying;
  }, [isPlaying]);

  // Touch: sem hover, o card visível roda mudo. Em qualquer device,
  // o vídeo completo para quando o card sai da tela.
  const onStopRef = useRef(onStop);
  onStopRef.current = onStop;
  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;
    const touch = !canHover();

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (playingRef.current) {
          if (entry.intersectionRatio < 0.25) onStopRef.current();
          return;
        }
        if (!touch) return;
        if (entry.intersectionRatio >= 0.75) {
          playPreview();
        } else {
          element.pause();
        }
      },
      { threshold: [0.25, 0.75] }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Link "esticado" (::after cobre o card): controles do vídeo ficam por
  // cima dele, sem aninhar elementos interativos dentro do <a>
  const link = (
    <Link
      prefetch={false}
      href={href}
      className={styles.link}
      aria-label={title ? undefined : "Ver coleção"}
    >
      {title}
    </Link>
  );

  const running = isPlaying && !paused;
  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className={classNames(styles.collection, {
        [styles.playing]: isPlaying,
        [styles.running]: running,
        // Vídeo rodando (mudo no hover/mobile ou com som): esconde a capa
        [styles.active]: showingVideo,
      })}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
    >
      <div className={styles.media}>
        {video ? (
          <>
            <video
              ref={videoRef}
              muted
              loop={!isPlaying}
              playsInline
              preload="none"
              aria-hidden="true"
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
              onPlay={() => setPaused(false)}
              onPlaying={() => setShowingVideo(true)}
              onPause={() => {
                setPaused(true);
                setShowingVideo(false);
              }}
              onEnded={onStop}
            >
              <source src={video} type="video/mp4" />
            </video>

            {/* Capa sobre o vídeo (no lugar do atributo poster): corte seco,
                some quando o vídeo começa a exibir frames e volta ao pausar */}
            {image && (
              <img className={styles.poster} src={image} alt="" loading="lazy" />
            )}

            {/* Com som, clicar em qualquer ponto do vídeo pausa/continua.
                O botão de play da barra é o equivalente acessível. */}
            {isPlaying && (
              <div
                className={styles.surface}
                onClick={togglePause}
                aria-hidden="true"
              />
            )}

            <div className={styles.controls}>
              {isPlaying && (
                <input
                  type="range"
                  className={styles.progress}
                  min={0}
                  max={duration || 0}
                  step={1}
                  value={currentTime}
                  onChange={handleSeek}
                  aria-label="Progresso do vídeo"
                  aria-valuetext={`${formatTime(currentTime)} de ${formatTime(
                    duration
                  )}`}
                  style={{ "--progress": `${progress}%` } as CSSProperties}
                />
              )}
              <div className={styles.controlsRow}>
                <button
                  type="button"
                  className={styles.playButton}
                  onClick={handlePlayButton}
                  aria-label={
                    !isPlaying
                      ? title
                        ? `Assistir vídeo: ${title}`
                        : "Assistir vídeo"
                      : running
                      ? "Pausar vídeo"
                      : "Continuar vídeo"
                  }
                >
                  {running ? <IoPause /> : <IoPlay />}
                </button>
                {duration > 0 && (
                  <span className={styles.time}>
                    {isPlaying
                      ? `${formatTime(currentTime)} / ${formatTime(duration)}`
                      : formatTime(duration)}
                  </span>
                )}
              </div>
            </div>
          </>
        ) : (
          image && <img src={image} alt={title || ""} loading="lazy" />
        )}
      </div>

      {title ? (
        <div className={styles.title}>
          {avatar && (
            <div className={styles.avatar}>
              <img src={avatar} alt="" width={60} height={60} loading="lazy" />
            </div>
          )}
          <h3>{link}</h3>
        </div>
      ) : (
        link
      )}
    </div>
  );
}
